import React from 'react';

import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useRuleStore } from '../../state/ruleStore';
import Button from 'components/Button';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';

type Props = {
  open: boolean;
  step: number;
  steps: string[];
  onBack: () => void;
  onNext: () => void;
  onClose: () => void;
  onFinish: () => void;
  onTabChange?: (step: number) => void;
  canNext: boolean;
  canFinish: boolean;
  statusActive: boolean;
  onToggleActive: (v: boolean) => void;
  isCreating?: boolean;
  isEditMode?: boolean; // ✅ Para determinar textos dos botões
  children: React.ReactNode;
};

export const WizardLayout: React.FC<Props> = ({
  open,
  step,
  steps,
  onBack,
  onNext,
  onClose,
  onFinish,
  onTabChange,
  canNext,
  canFinish,
  statusActive,
  onToggleActive,
  isCreating = false,
  isEditMode = false,
  children,
}) => {
  const theme = useThemeContext();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // Obter estados de loading do Zustand
  const { loading } = useRuleStore();
  const isLoading =
    loading.isLoadingGeneral ||
    loading.isLoadingTrigger ||
    loading.isLoadingConditions ||
    loading.isLoadingActions ||
    loading.isLoadingFinish;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          background: 'transparent',
          borderRadius: 4,
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          minHeight: 450,
          overflowX: 'hidden',
        },
      }}
    >
      <Tabs
        value={step}
        onChange={(_, newValue) => onTabChange?.(newValue)}
        sx={{
          minHeight: '48px',
          background: 'transparent',
          '& .MuiTab-root': {
            color: theme.text.primary,
            minWidth: isMobile ? 80 : 120,
            fontWeight: 500,
            fontSize: isMobile ? '0.875rem' : '1rem',
            textTransform: 'none',
            background: `${theme.background.secondary}`,
            borderRadius: '0.5rem 0.5rem 0 0',
          },
          '& .Mui-selected': {
            color: theme.text.primary,
            background: theme.background.secondary,
          },
          '& .MuiTabs-indicator': {
            background: theme.primary,
            height: 4,
          },
        }}
      >
        {steps.map((s, index) => (
          <Tab key={s} label={s} value={index} />
        ))}
      </Tabs>

      <DialogContent
        sx={{
          background: theme.background.secondary,
          borderRadius: '0 1rem 0 0',
        }}
      >
        <Box minWidth={400}>{children}</Box>
      </DialogContent>

      <DialogActions
        sx={{
          background: theme.background.secondary,
          borderRadius: '0 0 1rem 1rem',
          flexDirection: 'column',
          alignItems: 'stretch',
          py: 2,
        }}
      >
        {/* Feedback de loading */}
        {isLoading && loading.currentOperation && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              mb: 2,
              py: 1,
              px: 2,
            }}
          >
            <CircularProgress size={20} sx={{ color: theme.primary }} />
            <Typography
              variant="body2"
              sx={{
                color: theme.text.primary,
                fontWeight: 500,
              }}
            >
              {loading.currentOperation}
            </Typography>
          </Box>
        )}

        <Stack display="flex" width="100%" direction="row" justifyContent="space-between">
          <Stack display="flex" width="60%" direction="row" justifyContent="flex-start">
            {/* {step > 0 && (
              <Button onClick={onBack} variant="outline">
                Voltar
              </Button>
            )} */}
          </Stack>
          <Stack width="40%" spacing={2} direction="row" justifyContent="flex-end">
            <Button onClick={onClose} variant="outline" fullWidth disabled={isLoading}>
              Cancelar
            </Button>
            {step < steps.length - 1 ? (
              <Button
                onClick={() => {
                  console.log('🔍 Botão Próximo clicado');
                  console.log('🔍 canNext:', canNext);
                  console.log('🔍 isLoading:', isLoading);
                  console.log('🔍 isEditMode:', isEditMode);
                  onNext();
                }}
                disabled={!canNext || isLoading}
                fullWidth
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isLoading && <CircularProgress size={16} sx={{ color: 'inherit' }} />}
                  <span>
                    {isLoading
                      ? loading.currentOperation?.split('...')[0] || 'Processando...'
                      : isEditMode
                        ? 'Salvar e Continuar'
                        : 'Próximo'}
                  </span>
                </Box>
              </Button>
            ) : (
              <Button
                onClick={() => {
                  console.log('🔍 Botão Finalizar clicado');
                  console.log('🔍 canFinish:', canFinish);
                  console.log('🔍 isLoading:', isLoading);
                  console.log('🔍 isEditMode:', isEditMode);
                  onFinish();
                }}
                disabled={!canFinish || isLoading}
                fullWidth
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isLoading && <CircularProgress size={16} sx={{ color: 'inherit' }} />}
                  <span>
                    {isLoading
                      ? loading.currentOperation?.split('...')[0] || 'Finalizando...'
                      : isEditMode
                        ? 'Salvar Alterações'
                        : 'Criar Regra'}
                  </span>
                </Box>
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
